import {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import { Flex, Avatar, Icon, Button } from '@chakra-ui/react';
import { getTheWordObj } from '@/pages/interviews/utils/transcript';
import { Play } from '@kula-ai/icons/dual-tone/Play';
import { KText } from '@/ui-library/components/text';

interface Word {
  text: string;
  start_timestamp: number;
}

interface Speech {
  speaker: string;
  words: Word[];
}

interface TranscriptData {
  transcript?: Speech[];
}

interface TranscriptProps {
  currentTime?: number;
  data: TranscriptData;
  handleTimeJump: (_time: number, _shouldPlay?: boolean) => void;
  playerType?: 'mini' | 'modal';
}

interface CurrentIndex {
  sentence: number;
  word: number;
}

interface TranscriptRef {
  triggerFunction: () => void;
}

export const Transcript = forwardRef<TranscriptRef, TranscriptProps>(
  ({ currentTime = 0, data, handleTimeJump, playerType = 'mini' }, ref) => {
    const { transcript } = data || {};
    const [currentIndex, setCurrentIndex] = useState<CurrentIndex>({
      sentence: 0,
      word: 0,
    });
    const [previousIndex, setPreviousIndex] = useState<CurrentIndex | null>(
      null
    );
    const [userScrolling, setUserScrolling] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const previousTimeRef = useRef(currentTime);
    const [forceScroll, setForceScroll] = useState(false);

    // Fix 1: Detect manual scroll (wheel/touch events) and pause auto-scroll
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const handleManualScroll = () => {
        setUserScrolling(true);
      };

      container.addEventListener('wheel', handleManualScroll);
      container.addEventListener('touchmove', handleManualScroll);

      return () => {
        container.removeEventListener('wheel', handleManualScroll);
        container.removeEventListener('touchmove', handleManualScroll);
      };
    }, []);

    // Fix 3: Sync with video button handler
    const handleSyncWithVideo = () => {
      const element = document.getElementById(
        `word-${playerType}-${currentIndex?.sentence}-${currentIndex?.word}`
      );
      if (element && containerRef.current) {
        const container = containerRef.current;

        // Use a more reliable method: get element position relative to container
        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Calculate the element's position relative to the container
        const elementRelativeTop =
          elementRect.top - containerRect.top + container.scrollTop;

        // Calculate scroll position to center the element
        const scrollTop =
          elementRelativeTop -
          container.clientHeight / 2 +
          elementRect.height / 2;

        // Ensure scroll position is within valid bounds
        const maxScroll = container.scrollHeight - container.clientHeight;
        const validScrollTop = Math.max(0, Math.min(scrollTop, maxScroll));

        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          container.scrollTop = validScrollTop;
        });

        setUserScrolling(false);
      }
    };

    // Fix 2: Leave video-driven scroll logic unchanged, but respect userScrolling flag
    useEffect(() => {
      const element = document.getElementById(
        `word-${playerType}-${currentIndex?.sentence}-${currentIndex?.word}`
      );
      if (element) {
        // Always update highlighting
        element.style.background = '#605AFB';
        element.style.borderRadius = '3px';
        element.style.color = 'white';

        // Scroll if user is NOT manually scrolling, OR if timestamp was clicked (forceScroll)
        if (!userScrolling || forceScroll) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (forceScroll) {
            setForceScroll(false);
          }
        }
      }

      if (previousIndex !== null) {
        const previousElement = document.getElementById(
          `word-${playerType}-${previousIndex?.sentence}-${previousIndex?.word}`
        );
        if (previousElement) {
          previousElement.style.background = 'inherit';
          previousElement.style.color = 'inherit';
        }
      }
      setPreviousIndex(currentIndex);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex, userScrolling, forceScroll]);

    useEffect(() => {
      if (transcript) {
        const obj = getTheWordObj(transcript, currentTime);
        setCurrentIndex(obj?.indexes);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTime]);

    // Auto-sync when video is seeked (large time jump detected)
    // This handles clicking timestamps - forces scroll even if user manually scrolled
    useEffect(() => {
      const timeDiff = Math.abs(currentTime - previousTimeRef.current);
      // If time jumped by more than 500ms, treat as a timestamp click/seek
      if (timeDiff > 500) {
        setForceScroll(true);
        setUserScrolling(false);
      }
      previousTimeRef.current = currentTime;
    }, [currentTime]);

    const handleCopy = () => {
      const formattedContent = transcript
        ?.map(
          (speech) =>
            `${speech?.speaker}: ${speech?.words
              .map((word) => `${word?.text} `)
              .join('')}`
        )
        .join('\n\n');

      navigator.clipboard
        .writeText(formattedContent)
        .then(() => {
          console.log('Text copied!!');
        })
        .catch((err) => {
          console.error('Failed to copy text: ', err);
        });
    };

    // In this case, it allows the parent component to call the 'handleCopy' function, which copies the transcript text to the clipboard.
    // This is useful for providing a way for the parent component to trigger the copy action without needing to directly manage the state or logic of the child component.
    useImperativeHandle(ref, () => ({
      triggerFunction: handleCopy,
      // Reset manual scroll state when user touches video player
      resetManualScroll: () => {
        setUserScrolling(false);

        // Directly scroll to current position immediately
        if (containerRef.current && currentIndex) {
          const container = containerRef.current;
          const element = document.getElementById(
            `word-${playerType}-${currentIndex.sentence}-${currentIndex.word}`
          );

          if (element) {
            const elementRect = element.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const elementRelativeTop =
              elementRect.top - containerRect.top + container.scrollTop;
            const scrollTop =
              elementRelativeTop -
              container.clientHeight / 2 +
              elementRect.height / 2;
            const maxScroll = container.scrollHeight - container.clientHeight;
            const validScrollTop = Math.max(0, Math.min(scrollTop, maxScroll));

            // Scroll immediately
            container.scrollTop = validScrollTop;
          }
        }
      },
    }));

    return (
      <Flex flexDirection="column" flex={1} minH="0" position="relative">
        {/* Scrollable transcript content */}
        <Flex
          flexDirection="column"
          flex={1}
          overflowY="auto"
          mt="20px"
          pb="20px"
          ref={containerRef}
        >
          {transcript?.map((speech, index) => (
            <Flex
              key={'speech' + index}
              mb="20px"
              pos="relative"
              alignItems="center"
              _hover={{ '> div': { visibility: 'visible', opacity: 1 } }}
            >
              <Flex>
                <Avatar
                  name={speech?.speaker}
                  size="xs"
                  mr="12px"
                  borderRadius="md"
                />
                <Flex flexDirection="column">
                  <KText size="sm-semi-bold" mb="5px" color="secondary-900">
                    {speech?.speaker}
                  </KText>
                  <Flex wrap={'wrap'}>
                    {speech?.words.map((word, wordIndex) => (
                      <KText
                        mr="3px"
                        size="sm-regular"
                        id={`word-${playerType}-${index}-${wordIndex}`}
                        key={`word-${index}-${wordIndex}`}
                      >
                        {word?.text}
                      </KText>
                    ))}
                  </Flex>
                </Flex>
              </Flex>
              <Flex
                bg="rgba(96, 90, 251, 0.16);"
                w="26px"
                h="26px"
                borderRadius="50%"
                alignItems="center"
                justifyContent="center"
                pos="absolute"
                right="0"
                cursor="pointer"
                visibility="hidden"
                onClick={() =>
                  handleTimeJump(
                    speech?.words?.[0]?.start_timestamp * 1000,
                    true
                  )
                }
              >
                <Icon as={Play} w="12px" h="12px" color="#605AFB" />
              </Flex>
            </Flex>
          ))}
        </Flex>

        {/* Fix 3: Sync with Video button - floating at bottom */}
        {userScrolling && (
          <Flex
            position="absolute"
            bottom="2px"
            left="50%"
            transform="translateX(-50%)"
            zIndex={10}
          >
            <Button
              onClick={handleSyncWithVideo}
              size="sm"
              variant={'quaternary'}
              sx={{
                bg: 'secondary-950',
                color: 'white',
                border: 'none',
                _hover: { bg: 'secondary-950', color: 'white' },
                _active: { bg: 'secondary-950', color: 'white' },
              }}
            >
              Resume auto-scroll
            </Button>
          </Flex>
        )}
      </Flex>
    );
  }
);
